<template>
  <FormSection
    class="app-screenshots"
    :title="$t('APP.MEDIA.SCREENSHOTS')"
    :hint="$t('HINTS.APPS.MEDIA.SCREENSHOTS')"
  >
    <Card v-loading="!screenshots">
      <AppScreenshotsSelector
        v-loading="loading"
        :images="screenshotsModel"
        can-change-order
        @file-select="handleAddNewScreenshots"
        @delete="deleteScreenshot"
        @change="handleOrderChange"
      />
    </Card>
  </FormSection>
</template>

<script>
import FormSection from '@@/common/layout/FormSection';
import Card from '@@/common/Card';
import AppScreenshotsSelector from '../common/AppScreenshotsSelector';
import packageName from '@/mixins/packageName';
import packageNameWatch from '@/mixins/apps/packageNameWatch';
import { Apps } from '@/api/api';
import { UPDATE_SCREENSHOTS, SCREENSHOTS } from '@/store/types';
import { mapActions, mapGetters } from 'vuex';
import getFormData from '@/utils/getFormData';

export default {
  name: 'AppScreenshots',
  components: {
    FormSection,
    Card,
    AppScreenshotsSelector,
  },
  mixins: [packageName, packageNameWatch],

  data() {
    return {
      loading: false,
      screenshotsModel: [],
    };
  },

  computed: {
    ...mapGetters({ screenshots: SCREENSHOTS }),
  },

  watch: {
    screenshots(screenshots) {
      this.screenshotsModel = [...screenshots];
    },
  },

  created() {
    this.getInfo();
  },

  methods: {
    ...mapActions({
      dispatchUpdateScreenshots: UPDATE_SCREENSHOTS,
      dispatchScreenshots: SCREENSHOTS,
    }),

    getInfo() {
      this.getScreenshots();
    },

    getScreenshots() {
      if (!this.screenshots) {
        return this.dispatchScreenshots(this.packageName).catch(message => {
          if (message) {
            this.$message.error(message);
          }
        });
      }
    },

    handleAddNewScreenshots(images) {
      if (!images?.length) return;
      this.loading = true;
      const uploadPromises = images.map(image => this.uploadScreenshot(image));

      return Promise.allSettled(uploadPromises)
        .then(() => this.syncScreenshots())
        .finally(() => (this.loading = false));
    },

    deleteScreenshot(id) {
      this.screenshotsModel = this.screenshotsModel.filter(
        img => img.id !== id,
      );

      this.syncScreenshots();
    },

    handleOrderChange(screenshots) {
      this.screenshotsModel = [...screenshots];
      this.syncScreenshots();
    },

    uploadScreenshot(image) {
      const formData = getFormData([image], 'file');

      return Apps.uploadScreenshots({ packageName: this.packageName }, formData)
        .then(({ body: { id, message } }) => {
          this.$message.success(message);
          this.screenshotsModel.push({ id, type: 'new' });
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    syncScreenshots() {
      this.loading = true;

      const list = this.screenshotsModel.map(item => ({
        id: item.id,
        type: item.type || 'old',
      }));

      return this.dispatchUpdateScreenshots({
        packageName: this.packageName,
        list,
      })
        .catch(message => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
